<template>
    <tr>
        <table>
            <tr>
                <td>
                    <a
                        :href="DestinationTarget"
                        :data-lendername="LenderName"
                        class="lender-logo"
                        target="_blank"
                        @click="trackRateTableClickedEvent('LogoClicked/')">
                        <img
                            :alt="LenderName"
                            :src="LenderLogo"
                        />
                    </a>
                </td>
                <td>
                    <span class="val-title">APR</span>
                    <span class="values">{{ MinAPR }}% - {{ MaxAPR }}%</span>
                </td>
                <td class="monthly-pay">
                    <span class="val-title">
                        <span>Est. Payment</span>
                        <lt-label
                            show-tooltip="true"
                            show-question-mark="true"
                            :popper-content="tooltipContent()"
                        /></span>
                    <span class="values"> ${{ MonthlyPayment }}/mo.</span>
                </td>
                <td>
                    <div
                        class="offers-btn-block"
                        :data-lendername="LenderName">
                        <a
                            :href="DestinationTarget"
                            target="_blank"
                            class="destination-btn"
                            @click="trackRateTableClickedEvent('ButtonClicked/')"> {{ DestinationButtonText }}</a>
                    </div>
                </td>
            </tr>
        </table>
    </tr>
</template>

<script>
    import Accounting from 'accounting';
    import LtLabel from '~/components/lt-label.vue';
    import PaymentHelper from '~/helpers/payment';
    import wwwSplitterUrlEnv from '~/helpers/splitter-url-env';
    import RateTableInfo from '~/mixin/rate-table-offer-tracking';

    export default {
        components: {
            LtLabel,
        },
        mixins: [RateTableInfo],
        props: {
            offer: {
                default() {
                    return {};
                },
            },
            tooltipMonthlyPayment: {
                default: 'monthly-payment',
            },
            destinationButtonCssClass: {
                default: 'btn btn-orange',
            },
            destinationButtonText: {
                default: 'Get Free Offers',
            },
            loanAmount: {
                default: 0,
            },
            destinationTarget: {
                default: '',
            },
            showLowestApr: {
                default: true,
            },
            zipCode: {
                default: '',
            },
            creditBand: {
                default: '',
            },
            homeValue: {
                default: 0,
            },
            destinationLinkOrId: {
                default: '',
            },
            fieldEdited: {
                default: false,
            },
            ctaParams: {
                default: '',
            },
        },
        data() {
            return {
                DestinationButtonText: this.destinationButtonText,
                DestinationTarget: this.getDestinationTarget(this.offer),
                LenderId: this.offer.lenderId,
                MinAPR: this.offer.minAPR.toFixed(2),
                MaxAPR: this.offer.maxAPR.toFixed(2),
                MinInterestRate: this.offer.minInterestRate.toFixed(2),
                MaxInterestRate: this.offer.maxInterestRate.toFixed(2),
                MonthlyPayment: this.getMonthlyPayment(this.offer),
                LoanTermMonths: this.offer.loanTermMonths / 12,
                LoanAmount: this.loanAmount,
                LoanProgram: this.offer.loanProgram,
                LenderLogo: this.offer.lenderLogo,
                LenderUrl: this.offer.lenderUrl,
                LenderName: this.offer.lenderShortName,
                LoanType: this.offer.productCategory,
                ZipCode: this.zipCode,
                PropertyUse: this.offer.propertyUse,
                CreditBand: this.creditBand,
                HomeValue: this.homeValue,
                FieldEdited: this.offer.fieldEdited,
                Position: this.offer.position,
            };
        },
        methods: {
            tooltipContent() {
                return `<div class="popup-content"><p>A $${this.MonthlyPayment} monthly payment assumes $${this.LoanAmount} loan amount at ${this.offer.medianInterestRate}% (Median Interest Rate) over  (Loan term: For 15 years will be 180 months and for 30 years will be 360 months).</p></div>`;
            },
            getDestinationTarget(offer) {
                let splitterUrl = `${wwwSplitterUrlEnv.getSplitterURL()}/api/split?`;
                const queryParams = new URLSearchParams();
                queryParams.append('icid', 'ratetable');
                queryParams.append('PropertyUse', this.getTargetPropertyUse(offer.propertyUse));
                queryParams.append('EstimatedCreditRating', this.getTargetCreditRating(offer.selfGradedCredit));
                queryParams.append('EstimatedPropertyValue', this.homeValue);
                splitterUrl += queryParams.toString();
                const loanType = offer.productCategory.toLowerCase();
                if (this.destinationLinkOrId === '') {
                    if (loanType === 'purchase') {
                        splitterUrl += '&id=wp-mortgage';
                    } else {
                        splitterUrl += `&id=wp-refinance&PropertyAddressZipCode=${this.zipCode}`;
                    }
                } else if (/^[A-z0-9-]+$/i.test(this.destinationLinkOrId)) {
                    splitterUrl += `&id=${this.destinationLinkOrId}`;
                } else {
                    splitterUrl = `${this.destinationLinkOrId}`;
                }

                if (this.ctaParams) {
                    splitterUrl += `&${this.ctaParams}`;
                }
                return splitterUrl;
            },
            getMonthlyPayment(offer) {
                const monthlyPayment = PaymentHelper.getMonthlyPrincipalAndIntreset(this.loanAmount, offer.loanTermMonths, PaymentHelper.convertInterestRatePerMonths(offer.medianInterestRate));
                return Accounting.formatNumber(monthlyPayment.toFixed(0));
            },
            getTargetPropertyUse(propertyUse) {
                switch (propertyUse.toLowerCase()) {
                    case 'primary home': return 'OWNEROCCUPIED';
                    case 'secondary home': return 'SECONDHOME';
                    case 'investment property': return 'INVESTMENTPROPERTY';
                    default: return '';
                }
            },
            getTargetCreditRating(creditBand) {
                switch (creditBand.toLowerCase()) {
                    case 'excellent': return 'EXCELLENT';
                    case 'good': return 'SOMECREDITPROBLEMS';
                    case 'fair': return 'MAJORCREDITPROBLEMS';
                    case 'poor': return 'LITTLEORNOCREDITHISTORY';
                    default: return '';
                }
            },
            trackRateTableClickedEvent(clicked) {
                this.ClickLocation = clicked;
                this.rateTableClickedInfo(this);
            },
        },
    };
</script>
<style lang="scss" scoped>
@import "../../../assets/styles/grove/grove-colors";
@import "../../../assets/styles/grove/grove-sizes";
@import "../../../assets/styles/grove/buttons";

.destination-btn {
    @include button-common;
    @include button-size-medium;
    @include button-primary-blue;
    color: $white !important;
    width: 100%;
}
</style>
